import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { TrendingUp, TrendingDown, Minus, Users, CalendarDays, Package, Activity, Pencil, Check, X, AlertTriangle, Zap, Lock, Clock, BarChart3, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function DashboardPage() {
  const { currentProject } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [health, setHealth] = useState(null);
  const [editingKpi, setEditingKpi] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSummary();
    fetchHealth();
  }, [currentProject]);

  const fetchSummary = async () => {
    try {
      const res = await api.get('/dashboard/summary', { params: { project_id: currentProject?.id } });
      setSummary(res.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  const fetchHealth = async () => {
    try {
      const res = await api.get('/dashboard/health');
      setHealth(res.data);
    } catch { /* ignore */ }
  };

  const saveKpiValue = async (kpi) => {
    try {
      await api.patch(`/kpis/${kpi.id}`, { current_value: parseFloat(editValue) });
      toast.success('KPI aggiornato');
      setEditingKpi(null);
      fetchSummary();
    } catch { toast.error('Errore aggiornamento KPI'); }
  };

  const getProgress = (kpi) => {
    const target = kpi.target_m1 || 1;
    return Math.round((kpi.current_value / target) * 100);
  };

  const getTrend = (kpi) => {
    const pct = getProgress(kpi);
    if (pct >= 80) return { icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-50' };
    if (pct >= 50) return { icon: Minus, color: 'text-amber-600', bg: 'bg-amber-50' };
    return { icon: TrendingDown, color: 'text-red-600', bg: 'bg-red-50' };
  };

  const formatValue = (kpi) => {
    if (kpi.unit === 'currency') return `${kpi.current_value.toLocaleString('it-CH')} CHF`;
    if (kpi.unit === 'percentage') return `${kpi.current_value}%`;
    return kpi.current_value.toLocaleString('it-CH');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#1A3A5C] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-in" data-testid="dashboard-page">
      <div className="page-header">
        <h1 className="page-title">Dashboard KPI</h1>
        <p className="page-subtitle">Monitoraggio delle performance del progetto {currentProject?.name}</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="border-[#E2E8F0]">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="w-11 h-11 rounded-lg bg-[#1A3A5C]/10 flex items-center justify-center"><Users className="w-5 h-5 text-[#1A3A5C]" /></div>
            <div><p className="text-xs text-[#64748B] font-medium">Contatti CRM</p><p className="text-2xl font-bold text-[#1A3A5C]" data-testid="contacts-count">{summary?.contacts_count || 0}</p></div>
          </CardContent>
        </Card>
        <Card className="border-[#E2E8F0]">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="w-11 h-11 rounded-lg bg-[#2E75B6]/10 flex items-center justify-center"><CalendarDays className="w-5 h-5 text-[#2E75B6]" /></div>
            <div><p className="text-xs text-[#64748B] font-medium">Eventi Attivi</p><p className="text-2xl font-bold text-[#1A3A5C]" data-testid="events-count">{summary?.events_count || 0}</p></div>
          </CardContent>
        </Card>
        <Card className="border-[#E2E8F0]">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="w-11 h-11 rounded-lg bg-emerald-50 flex items-center justify-center"><Package className="w-5 h-5 text-emerald-600" /></div>
            <div><p className="text-xs text-[#64748B] font-medium">Prodotti Attivi</p><p className="text-2xl font-bold text-[#1A3A5C]" data-testid="products-count">{summary?.products_count || 0}</p></div>
          </CardContent>
        </Card>
      </div>

      {/* KPI Grid */}
      <h2 className="text-lg font-semibold text-[#1A3A5C] mb-4">KPI del Progetto</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
        {(summary?.kpis || []).map((kpi) => {
          const trend = getTrend(kpi);
          const TrendIcon = trend.icon;
          const pct = getProgress(kpi);
          return (
            <Card key={kpi.id} className="kpi-card" data-testid={`kpi-card-${kpi.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-xs font-medium text-[#64748B] uppercase tracking-wider">{kpi.name}</p>
                  {editingKpi === kpi.id ? (
                    <div className="flex items-center gap-2 mt-2">
                      <Input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} className="w-28 h-8 text-sm" data-testid={`kpi-edit-input-${kpi.id}`} />
                      <button onClick={() => saveKpiValue(kpi)} className="text-emerald-600 hover:text-emerald-700" data-testid={`kpi-save-${kpi.id}`}><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditingKpi(null)} className="text-red-500 hover:text-red-600" data-testid={`kpi-cancel-${kpi.id}`}><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-3xl font-bold text-[#0F172A]">{formatValue(kpi)}</p>
                      <button onClick={() => { setEditingKpi(kpi.id); setEditValue(String(kpi.current_value)); }} className="text-[#64748B] hover:text-[#1A3A5C] transition-colors" data-testid={`kpi-edit-btn-${kpi.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>
                <div className={`w-9 h-9 rounded-lg ${trend.bg} flex items-center justify-center`}>
                  <TrendIcon className={`w-4 h-4 ${trend.color}`} />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-[#64748B]">Target M1: {kpi.target_m1?.toLocaleString('it-CH')}</span>
                  <span className={`font-semibold ${pct >= 80 ? 'text-emerald-600' : pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{pct}%</span>
                </div>
                <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-500 ${pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* System Health */}
      {health && (
        <>
          <h2 className="text-lg font-semibold text-[#1A3A5C] mb-4">Salute Sistema</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className={`border-[#E2E8F0] ${health.pending_timeout > 0 ? 'border-red-300 bg-red-50' : ''}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <AlertTriangle className={`w-5 h-5 ${health.pending_timeout > 0 ? 'text-red-600' : 'text-emerald-600'}`} />
                <div><p className="text-xs text-[#64748B]">Pending Timeout</p><p className="text-xl font-bold text-[#0F172A]" data-testid="health-pending-timeout">{health.pending_timeout}</p></div>
              </CardContent>
            </Card>
            <Card className="border-[#E2E8F0]">
              <CardContent className="p-4 flex items-center gap-3">
                <Clock className={`w-5 h-5 ${health.timeout_24h > 0 ? 'text-amber-600' : 'text-emerald-600'}`} />
                <div><p className="text-xs text-[#64748B]">Timeout 24h</p><p className="text-xl font-bold text-[#0F172A]" data-testid="health-timeout-24h">{health.timeout_24h}</p></div>
              </CardContent>
            </Card>
            <Card className="border-[#E2E8F0]">
              <CardContent className="p-4 flex items-center gap-3">
                <Zap className="w-5 h-5 text-[#2E75B6]" />
                <div><p className="text-xs text-[#64748B]">Agente lento</p><p className="text-sm font-bold text-[#0F172A] truncate" data-testid="health-slowest">{health.slowest_agent || '-'}</p><p className="text-xs text-[#94A3B8]">{health.slowest_latency_sec > 0 ? `${health.slowest_latency_sec}s` : '-'}</p></div>
              </CardContent>
            </Card>
            <Card className="border-[#E2E8F0]">
              <CardContent className="p-4 flex items-center gap-3">
                <Lock className="w-5 h-5 text-[#1A3A5C]" />
                <div><p className="text-xs text-[#64748B]">Lock Attivi</p><p className="text-xl font-bold text-[#0F172A]" data-testid="health-locks">{health.active_locks}</p></div>
              </CardContent>
            </Card>
            <Card className={`border-[#E2E8F0] ${health.overlapping_lock_contacts > 0 ? 'border-amber-300 bg-amber-50' : ''}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <AlertTriangle className={`w-5 h-5 ${health.overlapping_lock_contacts > 0 ? 'text-amber-600' : 'text-emerald-600'}`} />
                <div><p className="text-xs text-[#64748B]">Lock Sovrapposti</p><p className="text-xl font-bold text-[#0F172A]" data-testid="health-overlaps">{health.overlapping_lock_contacts}</p></div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Recent Activity */}
      <h2 className="text-lg font-semibold text-[#1A3A5C] mb-4">Attività Recenti</h2>
      <Card className="border-[#E2E8F0]">
        <CardContent className="p-0">
          {(summary?.recent_activity || []).length === 0 ? (
            <div className="p-8 text-center text-[#64748B] text-sm">Nessuna attività recente</div>
          ) : (
            <div className="divide-y divide-[#E2E8F0]">
              {(summary?.recent_activity || []).map((a, i) => (
                <div key={i} className="flex items-center gap-4 p-4" data-testid={`activity-${i}`}>
                  <div className="w-8 h-8 rounded-full bg-[#1A3A5C]/10 flex items-center justify-center flex-shrink-0">
                    <Activity className="w-4 h-4 text-[#1A3A5C]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0F172A] truncate">{a.summary}</p>
                    <p className="text-xs text-[#64748B]">{a.agent_name} — {a.action_type}</p>
                  </div>
                  <span className="text-xs text-[#94A3B8] flex-shrink-0">{new Date(a.created_at).toLocaleDateString('it-CH')}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick links alle pagine rimosse dalla sidebar */}
      <div className="flex flex-wrap gap-2 mt-6" data-testid="dashboard-quick-links">
        {[
          { label: 'Pulse Map', to: '/pulse', icon: Zap },
          { label: 'Analisi Settimanale', to: '/analytics', icon: BarChart3 },
          { label: 'Agenda', to: '/agenda', icon: CalendarDays },
          { label: 'Profili Social', to: '/social-profiles', icon: Globe },
        ].map(link => {
          const LinkIcon = link.icon;
          return (
            <button key={link.to} onClick={() => navigate(link.to)}
              className="flex items-center gap-1.5 p-2 rounded-lg bg-white border border-gray-100 hover:border-[#2E75B6] hover:shadow-sm transition-all text-xs text-gray-500"
              data-testid={`quick-link-${link.to.slice(1)}`}>
              <LinkIcon className="w-3.5 h-3.5" />
              {link.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
